import { LANDSCAPE_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/landscapeBackdrops';
import type { AtmospherePreset } from '../presets.config';
import { ATMOSPHERE_PALETTE as P } from '../palette';
import { breathingGlowBlobs, gustingWeather, multiPlaneParallax, biomeCloser } from '../presetBuilders';

/** Swamp / Murkwater — a fetid, oppressive bog: a sickly green-teal haze sky, drifting will-o-wisp
 *  motes (sickly green/yellow), dim swamp light-shafts, a bubbling marsh-glow pooling at the floor,
 *  a murky energy bed, rising miasma, a pulsing bog flicker, drooping cypress/willow trunk
 *  silhouettes in murky green-black, HEAVY low-lying fog bands, and a dank green grade. Darker +
 *  sicklier than the cozy forest — humid, dank, oppressive (not enchanted). */
export const SWAMP: AtmospherePreset = {
  id: 'swamp',
  layers: [
    // Murky sky backplane — dark green-black zenith → sickly teal-green horizon, so the
    // drooping-trunk silhouettes below read as real depth instead of dark-on-black.
    { kind: 'skyGradient', from: 0x0a140e, to: 0x1d3a2c },
    // Will-o-wisp motes adrift in the bog — sickly green/yellow, slow + few.
    {
      kind: 'fireflies',
      count: 20, colors: [0x9bc24a, 0xc4d24a, 0x4a8f6e],
      sizeMin: 5, sizeMax: 14, driftPx: 30,
      periodMinMs: 6000, periodMaxMs: 13000, alphaMin: 0.18, alphaMax: 0.7,
    },
    // Dim, sickly swamp shafts filtering through the canopy (eerie, not warm).
    {
      kind: 'godRays',
      count: 3, color: 0x6b9e4a, widthMin: 50, widthMax: 130, height: 500,
      tiltDeg: 6, periodMinMs: 7000, periodMaxMs: 13000, alphaMin: 0.08, alphaMax: 0.3,
    },
    // Bubbling marsh-glow pooling at the floor + a dim sickly haze above — the bog breathing.
    // Marsh-glow swells and recedes (slow, sickly breathe).
    breathingGlowBlobs(
      [
        { color: 0x6fae3e, atX: 0.5, atY: 0.97, size: 580, alpha: 0.15 }, // marsh-floor glow
        { color: 0x3e9e7a, atX: 0.5, atY: 0.1, size: 400, alpha: 0.06 },
      ],
      { periodMs: 5400, min: 0.65, max: 1.4 },
    ),
    // Murky stagnant energy bed.
    {
      kind: 'lightField',
      count: 14, palette: [0x4a8f6e, 0x6fae3e, 0x2a5a4a],
      sizeMin: 110, sizeMax: 280, alphaMin: 0.05, alphaMax: 0.16,
      driftRadius: 60, periodMinMs: 6500, periodMaxMs: 12000,
    },
    // Rising miasma — sickly fumes lifting off the bog (sickly green → fade to murky dark), density gusting.
    // The miasma billows then thins on a slow, offset cycle.
    gustingWeather(
      { mode: 'rise', color: 0x6b9e4a, colorEnd: 0x1a3322,
        size: 13, frequencyMs: 130, lifespanMs: 4200, speed: 24, gravityY: -13, windX: 12, alphaEnd: 0.5 },
      { periodMs: 7200, min: 0.5, max: 1.55, phase: 0.2 },
    ),
    // Pulsing bog flicker at the floor (bubbling will-o-wisp pools).
    {
      kind: 'flicker', color: 0x7fa83c, size: 280, baseAlpha: 0.08, flickerAmp: 0.06, hzMin: 2, hzMax: 6,
      anchors: [{ atX: 0.2, atY: 0.9 }, { atX: 0.5, atY: 0.95 }, { atX: 0.8, atY: 0.9 }],
    },
    // Drooping cypress/willow trunks (PX) read against the murky teal horizon — far/mid/near
    // silhouettes in murky green-black. Far is tallest + sways gently; near is shortest + most opaque.
    ...multiPlaneParallax([
      { svg: LANDSCAPE_BACKDROPS.marsh.far, shape: 'ridge', color: P.moss, speed: 0.04, height: 440, alpha: 0.72,
        detail: 0.8, sway: { ampPx: 12, periodMs: 28000 } },
      { svg: LANDSCAPE_BACKDROPS.marsh.mid, shape: 'columns', color: 0x122a1b, speed: 0.10, height: 300, alpha: 0.86,
        detail: 1.2 },
      { svg: LANDSCAPE_BACKDROPS.marsh.near, shape: 'columns', color: P.forest, speed: 0.20, height: 190, alpha: 0.95,
        detail: 1 },
    ]),
    // HEAVY low-lying fog — thick haze hugging the bog floor (more bands + higher alpha than other biomes).
    { kind: 'fog', color: 0x4a8f6e, bands: 6, alphaMin: 0.07, alphaMax: 0.17, driftPx: 50,
      periodMinMs: 9000, periodMaxMs: 16000, yMin: 0.55, yMax: 0.96 },
    ...biomeCloser({
      vignette: { color: 0x07120c, edgeAlpha: 0.4, washColor: 0x3e7a4a, washAlpha: 0.06 },
      // Dank green grade ('normal' keeps it DARK + sickly, not lifted toward white).
      grade: { top: 0x5a8a52, bottom: 0x0e2418, strength: 0.12, blend: 'normal' },
    }),
  ],
};
